import React, {useContext, useEffect, useState} from "react";
import {exportPatients, getAllPatients, PatientFilterConfig} from "./groupService";
import PageButton from "../../components/pageButton/pageButton";
import ToolTips, {ToolTipAlign} from "../../components/toolTips/toolTips";
import {getDefaultSortByConfig, PatientSortBy, PatientSortConfig, SelectPatient} from "./patientPreviewList";
import SearchForm from "../../components/forms/searchForm";
import PatientFilter, {PatientFilterOptionSetting, PatientFilterSetting, SubscribeStatus} from "./patientFilter";
import Spinner from "../../components/spinner/spinner";
import {Patient} from "./dataForPatients";
import {PromptContext} from "../promptContext";

enum TableHeader {
    patient,
    dob,
    gender,
    phone,
    email,
    status,
    openRate,
    lastAttempt
}

enum SortField {
    patientName = "patientName",
    dob = "dob",
    openRate = "openRate",
    lastAttempt = "lastAttempt",
}

// enum AlertType {
//     unsubscribe,
//     resubscribe
// }

const pageSize = 10

export default function PatientList() {
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [patientList, setPatientList] = useState<Array<Patient>>([])
    const [showSpinner, setShowSpinner] = useState<boolean>(false)
    const [selectedPatientList, setSelectedPatientList] = useState<Array<SelectPatient>>([])
    const [isSelectCurPage, setIsSelectCurPage] = useState<boolean>(false)
    const [showPatientFilter, setShowPatientFilter] = useState<boolean>(false)
    const [patientFilterSetting, setPatientFilterSetting] = useState<PatientFilterSetting>()
    const [searchKeyword, setSearchKeyword] = useState<string>("")

    const defaultSortConfig: PatientSortConfig = Object.assign({}, getDefaultSortByConfig(), {firstName: PatientSortBy.asc})
    const [sortByConfig, setSortByConfig] = useState<PatientSortConfig>(defaultSortConfig)

    const {showError} = useContext(PromptContext)

    useEffect(() => {
        // update sort and request all the data.
        if (page === 1) {
            if (patientFilterSetting || sortByConfig !== defaultSortConfig) {
                getPatients()
            }
        } else {
            setPage(1)
        }
    }, [sortByConfig, patientFilterSetting])

    useEffect(() => {
        getPatients()
    }, [page])

    const onGetPatients = (patientList: Array<Patient>, total: number) => {
        const unselectedPatient = patientList.find((item) => selectedPatientList.find((pt) => pt.patientId === item.patientId) === undefined)
        setIsSelectCurPage(unselectedPatient === undefined)

        setPatientList(patientList)
        setTotal(total)
    }

    const preparePatientFilterConfig = (): PatientFilterConfig => {
        const statuses = patientFilterSetting?.status ? [patientFilterSetting.status.id] : [SubscribeStatus.unsubscribed, SubscribeStatus.subscribed]
        const openRate = patientFilterSetting?.openRate?.id

        let sortField = SortField.patientName, isAscending = true
        if (sortByConfig.firstName !== PatientSortBy.nosort) {
            sortField = SortField.patientName
            isAscending = sortByConfig.firstName === PatientSortBy.asc
        } else if (sortByConfig.dob !== PatientSortBy.nosort) {
            sortField = SortField.dob
            isAscending = sortByConfig.dob === PatientSortBy.asc
        } else if (sortByConfig.openRate !== PatientSortBy.nosort) {
            sortField = SortField.openRate
            isAscending = sortByConfig.openRate === PatientSortBy.asc
        } else if (sortByConfig.lastAttempt !== PatientSortBy.nosort) {
            sortField = SortField.lastAttempt
            isAscending = sortByConfig.lastAttempt === PatientSortBy.asc
        }

        return {
            keyword: searchKeyword,
            statuses: statuses,
            openRate: openRate,
            sortField: sortField,
            isAscending: isAscending,
        }
    }

    const getPatients = () => {
        setShowSpinner(true)
        getAllPatients(
            preparePatientFilterConfig(),
            page,
            pageSize,
            (patientList, total) => {
                setShowSpinner(false)
                onGetPatients(patientList, total)
            }, (error) => {
                setShowSpinner(false)
                showError(error)
            })
    }

    const startPageNumber = ((page - 1) * pageSize + 1)
    const endPageNumber = (page) * pageSize >= total ? total : (page) * pageSize
    const pageRange = `${startPageNumber}-${endPageNumber}`
    const $pageIndex = total > 0 ? (
        <div className={"flex flex-row items-center text-sm text-gray-1 space-x-2"}>
            <p className={"text-black font-bold"}>{pageRange}</p>
            <p className={"text-gray-2"}>of</p>
            <span className={"text-gray-3"}>{total}</span>
        </div>) : null

    const onNextPage = () => {
        if (page * pageSize > total) {
            return
        }
        setPage(page + 1)
    }
    const onPreviousPage = () => {
        if (page > 1) {
            setPage(page - 1)
        }
    }

    const hasPreviousPage = (page > 1)
    const hasNextPage = !(page * pageSize > total)
    const $pageButton = (
        <div className={"flex flex-row items-center justify-center space-x-4"}>
            <PageButton enable={hasPreviousPage} rotate={"rotate-180"} onClick={onPreviousPage} />
            <PageButton enable={hasNextPage} onClick={onNextPage} />
        </div>
    )

    const $subscribeIcon = (
        <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path id="Fill 1" opacity="0.4" d="M9.53175 1.91309H4.47425C2.49675 1.91309 1.16675 3.30142 1.16675 5.36642V10.1323C1.16675 12.1914 2.49675 13.5798 4.47425 13.5798H9.53175C11.5092 13.5798 12.8334 12.1914 12.8334 10.1323V5.36642C12.8334 3.30142 11.5092 1.91309 9.53175 1.91309Z" fill="#219653"/>
            <path id="Fill 4" d="M6.3078 9.64119C6.17713 9.64119 6.04647 9.5916 5.94672 9.49185L4.56247 8.1076C4.36297 7.9081 4.36297 7.58494 4.56247 7.38602C4.76197 7.18652 5.08455 7.18594 5.28405 7.38544L6.3078 8.40919L8.7158 6.00119C8.9153 5.80169 9.23788 5.80169 9.43738 6.00119C9.63688 6.20069 9.63688 6.52385 9.43738 6.72335L6.66888 9.49185C6.56913 9.5916 6.43847 9.64119 6.3078 9.64119Z" fill="#219653"/>
        </svg>
    )

    const $unsubscribeIcon = (
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path id="Fill 1" opacity="0.4" d="M9.7815 1.5752H4.724C2.7465 1.5752 1.4165 2.96353 1.4165 5.02853V9.79436C1.4165 11.8535 2.7465 13.2419 4.724 13.2419H9.7815C11.759 13.2419 13.0832 11.8535 13.0832 9.79436V5.02853C13.0832 2.96353 11.759 1.5752 9.7815 1.5752Z" fill="#EB5757"/>
            <path id="Fill 4" d="M9.00911 8.441L7.97136 7.40383L9.00853 6.36666C9.20803 6.16775 9.20803 5.844 9.00853 5.64508C8.80903 5.44441 8.48644 5.445 8.28694 5.6445L7.24919 6.68166L6.21144 5.64333C6.01194 5.44383 5.68878 5.445 5.48928 5.64333C5.29036 5.84283 5.29036 6.16658 5.48928 6.3655L6.52761 7.40383L5.49161 8.43925C5.29211 8.63875 5.29211 8.9625 5.49161 9.16083C5.59136 9.26116 5.72144 9.31075 5.85211 9.31075C5.98336 9.31075 6.11344 9.26116 6.21319 9.16141L7.24919 8.12541L8.28753 9.16316C8.38728 9.26291 8.51736 9.3125 8.64803 9.3125C8.77869 9.3125 8.90936 9.26233 9.00911 9.16316C9.20861 8.96366 9.20861 8.6405 9.00911 8.441Z" fill="#EB5757"/>
        </svg>
    )

    // const renderSubscribeButton = () => {
    //     return (
    //         <div className={"flex flex-row items-center space-x-5"}>
    //             <Button
    //                 status={ButtonStatus.primary}
    //                 type={ButtonType.outline}
    //                 onClick={onClickUnsubscribe}
    //             >
    //                 <div className={"flex flex-row items-center"}>
    //                     {$clinicUnsubscribeIcon}
    //                     <span className={"ml-3"}>Unsubscribe</span>
    //                 </div>
    //             </Button>
    //             <Button
    //                 status={ButtonStatus.primary}
    //                 type={ButtonType.outline}
    //                 onClick={onClickResubscribe}
    //             >
    //                 <div className={"flex flex-row items-center"}>
    //                     {$subscribeIcon}
    //                     <span className={"ml-3"}>Resubscribe</span>
    //                 </div>
    //             </Button>
    //         </div>
    //     )
    // }

    const $pageView = (
        <div className={"flex flex-row items-center justify-between space-x-20"}>
            {$pageIndex}
            {$pageButton}
        </div>
    )

    const $footer = total > 0 ? (
        <div className={"flex flex-row items-center justify-end w-full pt-4"}>
            {/*{renderSubscribeButton()}*/}
            {$pageView}
        </div>
    ) : null

    const $statusCol = (value: string | number) => {
        let icon, title
        if (value === SubscribeStatus.subscribed) {
            icon = $subscribeIcon
            title = "Subscribed"
        } else if (value === SubscribeStatus.unsubscribed) {
            icon = $unsubscribeIcon
            title = "Unsubscribed"
        } else {
            return null
        }

        return (
            <div className={"flex flex-row items-center"}>
                {icon}
                <span className={"ml-1 text-gray-2 text-sm font-normal"}>{title}</span>
            </div>
        )
    }

    const $openRateCol = (value: number) => {
        const text = value === 0 ? "N/A" : `${Math.floor(value * 100)}%`
        return <p className={"w-16 break-all text-center flex flex-none text-gray-2 text-sm font-normal"}>{text}</p>
    }

    const $emailCol = (value: string | number) => {
        return (
            <div className={"flex flex-row items-center w-44"}>
                <p className={"w-full break-all text-gray-2 text-sm font-normal truncate"}>{value}</p>
            </div>
        )
    }

    const $dateCol = (value: string | number) => {
        return <p className={"w-24 break-all text-center flex flex-none text-gray-2 text-sm font-normal"}>{value}</p>
    }

    const $genderCol = (value: string) => {
        return <p className={"w-10 break-all text-center flex flex-none text-gray-2 text-sm font-normal"}>{value}</p>
    }

    const $checkCol = (title: string, checked: boolean, onChange?: () => void) => {
        if (!onChange) {
            return (
                <div className={"flex flex-row items-center w-44 py-3"}>
                    <p className={"w-full break-all text-gray-1 text-sm truncate"}>{title}</p>
                </div>
            )
        }
        return (
            <div className={"flex flex-row items-center w-40"}>
                <input checked={checked} onChange={() => {
                    onChange && onChange()
                }} type="checkbox" className={"form-checkbox h-3.5 w-3.5 rounded mr-4"} />
                <p className={"w-full break-all text-gray-1 text-sm"}>{title}</p>
            </div>
        )
    }

    const $phone = (icon: any, phoneNumber: string) => {
        return (
            <div className={"flex flex-row items-center space-x-1"}>
                {icon}
                <p className={"min-w-min flex-none text-gray-2 text-sm font-normal"}>{phoneNumber}</p>
            </div>
        )
    }

    const $iconMobilePhone = (
        <svg className={"flex-none"} width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_453_704)">
                <path d="M14.1005 10.4668L11.038 9.15432C10.9072 9.09856 10.7618 9.08681 10.6237 9.12084C10.4856 9.15487 10.3623 9.23283 10.2724 9.34299L8.91613 11C6.78762 9.99645 5.07466 8.28349 4.07109 6.15498L5.72812 4.79873C5.83851 4.70896 5.91663 4.58566 5.95067 4.44752C5.98472 4.30937 5.97283 4.16389 5.9168 4.03311L4.6043 0.970607C4.5428 0.829625 4.43405 0.714517 4.29678 0.645134C4.15951 0.57575 4.00233 0.556438 3.85234 0.590528L1.00859 1.24678C0.863992 1.28017 0.734977 1.36159 0.642607 1.47775C0.550237 1.5939 0.499967 1.73794 0.5 1.88635C0.5 8.90002 6.18476 14.5738 13.1875 14.5738C13.336 14.5739 13.4801 14.5237 13.5963 14.4313C13.7125 14.339 13.7939 14.2099 13.8273 14.0653L14.4836 11.2215C14.5175 11.0708 14.4977 10.913 14.4278 10.7752C14.3579 10.6375 14.2422 10.5284 14.1005 10.4668Z" fill="black"/>
            </g>
            <defs>
                <clipPath id="clip0_453_704">
                    <rect width="14" height="14" fill="white" transform="translate(0.5 0.57373)"/>
                </clipPath>
            </defs>
        </svg>
    )

    const $iconHomePhone = (
        <svg className={"flex-none"} width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_453_708)">
                <path d="M7.31466 3.95504L2.83345 7.64584V11.6293C2.83345 11.7324 2.87442 11.8313 2.94735 11.9043C3.02028 11.9772 3.1192 12.0182 3.22234 12.0182L5.94601 12.0111C6.04881 12.0106 6.14723 11.9694 6.21974 11.8965C6.29225 11.8237 6.33296 11.725 6.33296 11.6222V9.29595C6.33296 9.19281 6.37393 9.09389 6.44686 9.02096C6.51979 8.94803 6.6187 8.90706 6.72184 8.90706H8.2774C8.38054 8.90706 8.47945 8.94803 8.55238 9.02096C8.62531 9.09389 8.66629 9.19281 8.66629 9.29595V11.6205C8.66613 11.6717 8.67607 11.7224 8.69554 11.7697C8.71501 11.817 8.74363 11.8601 8.77976 11.8963C8.81589 11.9325 8.85881 11.9613 8.90607 11.9809C8.95333 12.0005 9.004 12.0106 9.05517 12.0106L11.7779 12.0182C11.881 12.0182 11.9799 11.9772 12.0529 11.9043C12.1258 11.8313 12.1668 11.7324 12.1668 11.6293V7.64317L7.68653 3.95504C7.63386 3.91259 7.56824 3.88943 7.50059 3.88943C7.43294 3.88943 7.36733 3.91259 7.31466 3.95504ZM14.3931 6.46362L12.3612 4.78872V1.42216C12.3612 1.34481 12.3305 1.27062 12.2758 1.21592C12.2211 1.16122 12.1469 1.13049 12.0695 1.13049H10.7084C10.6311 1.13049 10.5569 1.16122 10.5022 1.21592C10.4475 1.27062 10.4168 1.34481 10.4168 1.42216V3.18699L8.2407 1.39664C8.03187 1.22479 7.76982 1.13084 7.49938 1.13084C7.22893 1.13084 6.96689 1.22479 6.75806 1.39664L0.605606 6.46362C0.576072 6.48803 0.551637 6.51802 0.533698 6.55188C0.515758 6.58574 0.504666 6.6228 0.501054 6.66094C0.497443 6.69909 0.501383 6.73757 0.51265 6.7742C0.523916 6.81082 0.542289 6.84486 0.566717 6.87438L1.18651 7.62785C1.21087 7.65748 1.24083 7.682 1.27469 7.70003C1.30854 7.71806 1.34562 7.72923 1.38379 7.73291C1.42197 7.73659 1.4605 7.73271 1.49717 7.72148C1.53384 7.71025 1.56794 7.6919 1.59751 7.66747L7.31466 2.95851C7.36733 2.91606 7.43294 2.89291 7.50059 2.89291C7.56824 2.89291 7.63386 2.91606 7.68653 2.95851L13.4039 7.66747C13.4334 7.6919 13.4675 7.71027 13.5041 7.72154C13.5407 7.73281 13.5792 7.73675 13.6174 7.73314C13.6555 7.72952 13.6926 7.71843 13.7264 7.70049C13.7603 7.68255 13.7903 7.65812 13.8147 7.62858L14.4345 6.87511C14.4589 6.84542 14.4772 6.8112 14.4883 6.77442C14.4994 6.73764 14.5032 6.69901 14.4993 6.66078C14.4955 6.62254 14.4841 6.58543 14.4659 6.5516C14.4477 6.51776 14.423 6.48786 14.3931 6.46362Z" fill="black"/>
            </g>
            <defs>
                <clipPath id="clip0_453_708">
                    <rect width="14" height="12.4444" fill="white" transform="translate(0.5 0.351562)"/>
                </clipPath>
            </defs>
        </svg>
    )

    const $phoneCol = (mobilePhone: string, homePhone: string) => {
        return (
            <div>
                {mobilePhone && mobilePhone.length ? $phone($iconMobilePhone, mobilePhone) : null}
                {homePhone && homePhone.length ? $phone($iconHomePhone, homePhone) : null}
            </div>
        )
    }

    const isSelected = (patient: Patient) => {
        return selectedPatientList.find((pt) => pt.patientId === patient.patientId) !== undefined
    }

    // const onChangeSelectStatus = (patient: PatientPreview) => {
    //     let _selectedPatients = selectedPatientList.slice()
    //     if (isSelected(patient)) {
    //         _selectedPatients = _selectedPatients.filter((pt) => pt.patientId !== patient.patientId)
    //         setIsSelectCurPage(false)
    //     } else {
    //         const selectedPatient: SelectPatient = {
    //             patientId: patient.patientId,
    //             mpId: patient.mpid,
    //             accountId: patient.accountId,
    //         }
    //         _selectedPatients.push(selectedPatient)
    //
    //         const unselectedPatient = patientList.find((item) => _selectedPatients.find((pt) => pt.patientId === item.patientId) === undefined)
    //         setIsSelectCurPage(unselectedPatient === undefined)
    //     }
    //     setSelectedPatientList(_selectedPatients)
    // }

    const $activeCampaignContent = () => {
        return patientList.map((patientInfo, idx) => {
            const isChecked = isSelected(patientInfo)
            return (
                <tr className={"border-b border-border-black-12"} key={idx}>
                    <td className={"p-1"}>{$checkCol(patientInfo.patientName, isChecked)}</td>
                    <td className={"p-1"}>{$dateCol(patientInfo.dob)}</td>
                    <td className={"p-1 text-center"}>{$genderCol(patientInfo.gender)}</td>
                    <td className={"p-1"}>{$phoneCol(patientInfo.mobilePhone, patientInfo.homePhone)}</td>
                    <td className={"p-1"}>{$emailCol(patientInfo.email)}</td>
                    <td className={"p-1"}>{$statusCol(patientInfo.status)}</td>
                    <td className={"p-1"}>{$openRateCol(patientInfo.openRate)}</td>
                    <td className={"p-1"}>{$dateCol(patientInfo.lastAttempt)}</td>
                </tr>
            )
        })
    }

    const onClickFilter = () => {
        !showPatientFilter && setShowPatientFilter(true)
    }

    const $filterBtn = (
        <div className={"cursor-pointer"} onClick={onClickFilter}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M10.0801 18.5928H3.77905C3.36505 18.5928 3.02905 18.2568 3.02905 17.8428C3.02905 17.4288 3.36505 17.0928 3.77905 17.0928H10.0801C10.4941 17.0928 10.8301 17.4288 10.8301 17.8428C10.8301 18.2568 10.4941 18.5928 10.0801 18.5928Z" fill="#2F80ED"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M19.1909 8.90039H12.8909C12.4769 8.90039 12.1409 8.56439 12.1409 8.15039C12.1409 7.73639 12.4769 7.40039 12.8909 7.40039H19.1909C19.6049 7.40039 19.9409 7.73639 19.9409 8.15039C19.9409 8.56439 19.6049 8.90039 19.1909 8.90039Z" fill="#2F80ED"/>
                <mask id="mask0_2223_2433" maskUnits="userSpaceOnUse" x="3" y="5" width="7" height="7">
                    <path fillRule="evenodd" clipRule="evenodd" d="M3 5H9.2258V11.1916H3V5Z" fill="white"/>
                </mask>
                <g mask="url(#mask0_2223_2433)">
                    <path fillRule="evenodd" clipRule="evenodd" d="M6.11276 6.5C5.22376 6.5 4.49976 7.216 4.49976 8.097C4.49976 8.977 5.22376 9.692 6.11276 9.692C7.00276 9.692 7.72576 8.977 7.72576 8.097C7.72576 7.216 7.00276 6.5 6.11276 6.5ZM6.11276 11.192C4.39676 11.192 2.99976 9.804 2.99976 8.097C2.99976 6.39 4.39676 5 6.11276 5C7.82976 5 9.22576 6.39 9.22576 8.097C9.22576 9.804 7.82976 11.192 6.11276 11.192Z" fill="#2F80ED"/>
                </g>
                <path fillRule="evenodd" clipRule="evenodd" d="M17.3877 16.208C16.4977 16.208 15.7737 16.924 15.7737 17.804C15.7737 18.685 16.4977 19.4 17.3877 19.4C18.2767 19.4 18.9997 18.685 18.9997 17.804C18.9997 16.924 18.2767 16.208 17.3877 16.208ZM17.3877 20.9C15.6707 20.9 14.2737 19.511 14.2737 17.804C14.2737 16.097 15.6707 14.708 17.3877 14.708C19.1037 14.708 20.4997 16.097 20.4997 17.804C20.4997 19.511 19.1037 20.9 17.3877 20.9Z" fill="#2F80ED"/>
            </svg>
        </div>
    )

    const onClickExport = () => {
        exportPatients(preparePatientFilterConfig())
    }

    const $exportIcon = (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Paper Download">
                <g id="Paper Download">
                    <path id="Paper Download_2" fillRule="evenodd" clipRule="evenodd" d="M12.1535 16.64L14.995 13.77C15.2822 13.47 15.2822 13 14.9851 12.71C14.698 12.42 14.2327 12.42 13.9455 12.71L12.3713 14.31V9.49C12.3713 9.07 12.0446 8.74 11.6386 8.74C11.2327 8.74 10.896 9.07 10.896 9.49V14.31L9.32178 12.71C9.03465 12.42 8.56931 12.42 8.28218 12.71C7.99505 13 7.99505 13.47 8.28218 13.77L11.1139 16.64C11.1832 16.71 11.2624 16.76 11.3515 16.8C11.4406 16.84 11.5396 16.86 11.6386 16.86C11.7376 16.86 11.8267 16.84 11.9158 16.8C12.005 16.76 12.0842 16.71 12.1535 16.64ZM19.3282 9.02561C19.5609 9.02292 19.8143 9.02 20.0446 9.02C20.302 9.02 20.5 9.22 20.5 9.47V17.51C20.5 19.99 18.5 22 16.0446 22H8.17327C5.58911 22 3.5 19.89 3.5 17.29V6.51C3.5 4.03 5.4901 2 7.96535 2H13.2525C13.5 2 13.7079 2.21 13.7079 2.46V5.68C13.7079 7.51 15.1931 9.01 17.0149 9.02C17.4333 9.02 17.8077 9.02318 18.1346 9.02595C18.3878 9.02809 18.6125 9.03 18.8069 9.03C18.9479 9.03 19.1306 9.02789 19.3282 9.02561ZM19.6045 7.5661C18.7916 7.5691 17.8322 7.5661 17.1421 7.5591C16.047 7.5591 15.145 6.6481 15.145 5.5421V2.9061C15.145 2.4751 15.6629 2.2611 15.9579 2.5721C16.7203 3.37199 17.8873 4.5978 18.8738 5.63395C19.2735 6.05379 19.6436 6.44249 19.945 6.7591C20.2342 7.0621 20.0223 7.5651 19.6045 7.5661Z" fill="#2F80ED"/>
                </g>
            </g>
        </svg>
    )

    const $exportBtn = () => {
        return (
            <div className={"cursor-pointer"} onClick={() => {
                onClickExport()
            }}>
                {$exportIcon}
            </div>
        )
    }

    const $header = (
        <div className={"w-full flex flex-row justify-between items-center"}>
            <div className={"flex flex-row items-center"}>
                {selectedPatientList.length ? <p className={"text-sm text-gray-2"}>{`${selectedPatientList.length} patients selected.`}</p> : null}
            </div>
            <div className={"w-1/4 flex flex-row justify-end items-center space-x-5"}>
                <SearchForm
                    value={searchKeyword}
                    placeholder={"Search patient"}
                    onChangeText={(text) => {
                        setSearchKeyword(text)
                    }}
                    onFinishText={() => {
                        if (page === 1) {
                            getPatients()
                        } else {
                            setPage(1)
                        }
                    }}
                />
                {$filterBtn}
                {$exportBtn()}
            </div>
        </div>
    )

    const $filterItem = (setting?: PatientFilterOptionSetting, onRemove?: () => void) => {
        if (!setting) {
            return null
        }
        return (
            <div className={"flex flex-row items-center p-2 rounded-lg border border-primary"}>
                <p className={"text-sm text-gray-2"}>{`${setting.title}: ${setting.desc}`}</p>
                <div className={"ml-2 cursor-pointer"} onClick={() => {
                    onRemove && onRemove()
                }}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.9963 6.99609L7.00293 10.9894" stroke="#2F80ED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10.9975 10.9916L7.00085 6.99414" stroke="#2F80ED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M12.6121 1.29199H5.38795C2.87045 1.29199 1.29211 3.07449 1.29211 5.59699V12.4037C1.29211 14.9262 2.86295 16.7087 5.38795 16.7087H12.6113C15.1371 16.7087 16.7088 14.9262 16.7088 12.4037V5.59699C16.7088 3.07449 15.1371 1.29199 12.6121 1.29199Z" stroke="#2F80ED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
            </div>
        )
    }

    const $filter = () => {
        if (!patientFilterSetting) {
            return null
        }

        return (
            <div className={"w-full flex flex-row items-center space-x-4 pt-4"}>
                {$filterItem(patientFilterSetting.status, () => {
                    setPatientFilterSetting({
                        ...patientFilterSetting,
                        status: undefined,
                    })
                })}
                {$filterItem(patientFilterSetting.openRate, () => {
                    setPatientFilterSetting({
                        ...patientFilterSetting,
                        openRate: undefined,
                    })
                })}
            </div>
        )
    }

    const $iconArrow = (
        <svg width="7" height="11" viewBox="0 0 7 11" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_454_1044)">
                <path d="M0.694613 4.25H5.72C6.06766 4.25 6.24149 4.66992 5.99539 4.91602L3.48368 7.42969C3.33133 7.58203 3.08328 7.58203 2.93094 7.42969L0.419222 4.91602C0.173129 4.66992 0.346957 4.25 0.694613 4.25Z" fill="#333333"/>
            </g>
            <defs>
                <clipPath id="clip0_454_1044">
                    <rect width="6.25" height="10" fill="white" transform="translate(0.083313 0.5)"/>
                </clipPath>
            </defs>
        </svg>
    )

    const onChangeSortBy = (type: TableHeader) => {
        const newConfig = getDefaultSortByConfig()
        if (type === TableHeader.patient) {
            newConfig.firstName = sortByConfig.firstName === PatientSortBy.asc ? PatientSortBy.desc : PatientSortBy.asc
        } else if (type === TableHeader.dob) {
            newConfig.dob = sortByConfig.dob === PatientSortBy.asc ? PatientSortBy.desc : PatientSortBy.asc
        } else if (type === TableHeader.openRate) {
            newConfig.lastVisit = sortByConfig.lastVisit === PatientSortBy.asc ? PatientSortBy.desc : PatientSortBy.asc
        } else if (type === TableHeader.lastAttempt) {
            newConfig.lastAttempt = sortByConfig.lastAttempt === PatientSortBy.asc ? PatientSortBy.desc : PatientSortBy.asc
        }
        setSortByConfig(newConfig)
    }

    const onSelectPatients = () => {
        let _selectedPatients = selectedPatientList.slice()
        if (isSelectCurPage) {
            _selectedPatients = _selectedPatients.filter((item) => patientList.find((pt) => pt.patientId === item.patientId) === undefined)
        } else {
            const preSelectList = patientList.filter((item) => _selectedPatients.find((pt) => pt.patientId === item.patientId) === undefined)
            const patients: Array<SelectPatient> = preSelectList.map((pt) => ({
                clinicId: pt.clinicId,
                patientId: pt.patientId,
            }))
            _selectedPatients = _selectedPatients.concat(patients)
        }
        setSelectedPatientList(_selectedPatients)
        setIsSelectCurPage(!isSelectCurPage)
    }

    // const $patientToolTips = (
    //     <ToolTips tips={"Uncheck patients to exclude them from receiving messages."}>
    //         <p className={"cursor-default text-sm font-bold text-gray-1"}>Patient</p>
    //     </ToolTips>
    // )

    const $lastAttemptToolTips = (
        <ToolTips alignStyle={ToolTipAlign.right} tips={"The last time the practice dispatched an appointment office visit, phone encounter, or message for this patient."}>
            <p className={"cursor-default text-sm font-bold text-gray-1"}>Last Attempt</p>
        </ToolTips>
    )

    // const $statusToolTips = (
    //     <ToolTips tips={"Patient Unsubscribed: Action initiated by patients. Cannot resubscribe (CAN-SPAM Act)"}>
    //         <div className={"flex flex-row items-center"}>
    //             <p className={"cursor-default text-sm font-bold text-gray-1"}>Status</p>
    //             <div className={"ml-1"}>
    //                 <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    //                     <path opacity="0.4" d="M15.1663 8.49986C15.1663 12.1825 12.1817 15.1665 8.49967 15.1665C4.81767 15.1665 1.83301 12.1825 1.83301 8.49986C1.83301 4.81852 4.81767 1.83319 8.49967 1.83319C12.1817 1.83319 15.1663 4.81852 15.1663 8.49986Z" fill="#2F80ED"/>
    //                     <path fillRule="evenodd" clipRule="evenodd" d="M9.07975 8.92047C9.07975 9.24181 8.81775 9.50381 8.49642 9.50381C8.17509 9.50381 7.91309 9.24181 7.91309 8.92047V5.97381C7.91309 5.65247 8.17509 5.39047 8.49642 5.39047C8.81775 5.39047 9.07975 5.65247 9.07975 5.97381V8.92047ZM7.91642 11.0357C7.91642 10.7143 8.17709 10.4523 8.49642 10.4523C8.82509 10.4523 9.08642 10.7143 9.08642 11.0357C9.08642 11.357 8.82509 11.619 8.50309 11.619C8.17975 11.619 7.91642 11.357 7.91642 11.0357Z" fill="#2F80ED"/>
    //                 </svg>
    //             </div>
    //         </div>
    //     </ToolTips>
    // )

    const renderTableHeaderTitle = (type: TableHeader, title: string) => {
        // if (type === TableHeader.patient) {
        //     return $patientToolTips
        // } else if (type === TableHeader.lastAttempt) {
        //     return $lastAttemptToolTips
        // } else if (type === TableHeader.status) {
        //     return $statusToolTips
        // }
        if (type === TableHeader.lastAttempt) {
            return $lastAttemptToolTips
        }
        return <p className={"w-max font-bold text-sm text-gray-1"}>{title}</p>
    }

    const renderCheckbox = (type?: TableHeader) => {
        if (type === TableHeader.patient) {
            return <input checked={isSelectCurPage} onChange={onSelectPatients} type="checkbox" className="form-checkbox h-3.5 w-3.5 rounded mr-4" />
        }
        return null
    }

    const renderTableHeaderItem = (type: TableHeader, title: string, sortable: boolean = false, sortBy: PatientSortBy = PatientSortBy.nosort) => {
        const $sortBtn = (
            <button type={"button"} onClick={() => {
                onChangeSortBy(type)
            }} className={`p-2 rounded-full ${sortBy === PatientSortBy.nosort ? "opacity-0 group-hover:opacity-100 bg-primary-light" : "hover:bg-primary-light"} cursor-pointer`}>
                <div className={`w-4 h-4 flex items-center justify-center transform ${sortBy === PatientSortBy.asc ? "rotate-180" : "rotate-0"}`}>
                    {$iconArrow}
                </div>
            </button>
        )
        return (
            <th>
                <div className={"relative w-full my-4 px-1 flex flex-row items-center justify-between"}>
                    {renderCheckbox()}
                    <div className={"flex-1 flex flex-row items-center justify-between group"}>
                        {renderTableHeaderTitle(type, title)}
                        {sortable ? $sortBtn : null}
                    </div>
                    {/*{type !== TableHeader.lastAttempt ? <div className={"w-0.5 h-5 bg-gray-200 absolute right-0"}/> : null}*/}
                </div>
            </th>
        )
    }

    const $tableHeader = (
        <tr className={"text-left text-gray-2 font-bold border-b border-base-border text-sm"}>
            {renderTableHeaderItem(TableHeader.patient, "Patient", true, sortByConfig.firstName)}
            {renderTableHeaderItem(TableHeader.dob, "DOB", true, sortByConfig.dob)}
            {renderTableHeaderItem(TableHeader.gender, "Gender")}
            {renderTableHeaderItem(TableHeader.phone, "Phone")}
            {renderTableHeaderItem(TableHeader.email, "Email")}
            {renderTableHeaderItem(TableHeader.status, "Status")}
            {renderTableHeaderItem(TableHeader.openRate, "Open Rate", true, sortByConfig.openRate)}
            {renderTableHeaderItem(TableHeader.lastAttempt, "Last Attempt", true, sortByConfig.lastAttempt)}
        </tr>
    )

    const $table = (
        <table className={"w-full"}>
            <thead>
                {$tableHeader}
            </thead>
            <tbody>
                {$activeCampaignContent()}
            </tbody>
        </table>
    )

    const $spinner = <Spinner isOpen={showSpinner}/>

    const $patientFilterModal = (
        <PatientFilter
            isOpen={showPatientFilter}
            filterSetting={patientFilterSetting}
            onCancel={() => {
                setShowPatientFilter(false)
            }}
            onApply={(setting) => {
                setShowPatientFilter(false)
                setPatientFilterSetting(setting)
            }}
        />
    )

    return (
        <div className={"w-full min-w-min mt-4 p-6 flex flex-col items-center bg-white rounded"}>
            {$header}
            {$filter()}
            {$table}
            {$footer}
            {$spinner}
            {$patientFilterModal}
        </div>
    )
}
